import type { ReactNode } from 'react'
import type { UniqueIdentifier } from '@dnd-kit/core'
import type { MediaFile } from '@/lib/services/media/media.types'
import type { LightboxTriggerProps } from './ImageLightboxGallery'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { mergeEditedMedia } from '@/lib/media-editing'
import { animateLayoutChanges } from '@/lib/utils'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import UploadedMediaPreviewTile from './UploadedMediaPreviewTile'

type SortablePreviewState = Pick<
  ReturnType<typeof useSortable>,
  'attributes' | 'listeners' | 'setNodeRef' | 'transform' | 'transition'
>

interface ImagePreviewProps {
  media: MediaFile
  index: number
  lightboxTriggerProps?: LightboxTriggerProps
}

const SortableImagePreview = ({
  id,
  disabled,
  children,
}: {
  id: UniqueIdentifier
  disabled: boolean
  children: (sortable: SortablePreviewState) => ReactNode
}) => {
  const sortable = useSortable({ id, animateLayoutChanges, disabled })

  return children(sortable)
}

const ImagePreview = ({ media, index, lightboxTriggerProps = undefined }: ImagePreviewProps) => {
  const { isReadOnly } = useIsReadOnly()

  const field = useFieldContext<(MediaFile | undefined)[] | null | undefined>()
  const handleEditComplete = (editedMedia: MediaFile) => {
    const currentMedia = Array.isArray(field.state.value) ? field.state.value : []
    const nextMedia = [...currentMedia]
    nextMedia[index] = mergeEditedMedia(nextMedia[index], editedMedia)
    field.setValue(nextMedia)
  }
  const renderPreview = (sortable?: SortablePreviewState) => {
    const interactionProps =
      !isReadOnly && sortable ? { ...sortable.attributes, ...sortable.listeners } : undefined
    const mergedLightboxTriggerProps = lightboxTriggerProps
      ? { ...lightboxTriggerProps, ...interactionProps }
      : undefined

    return (
      <li
        id={media?.id?.toString()}
        ref={sortable?.setNodeRef}
        style={{
          transform: CSS.Transform.toString(sortable?.transform ?? null),
          transition: sortable?.transition,
        }}
        className="inline-flex items-center touch-none"
      >
        <UploadedMediaPreviewTile
          media={media}
          alt={media?.name || 'Uploaded image'}
          caption={media?.name}
          onRemove={!isReadOnly ? () => field.removeValue(index) : null}
          onEditComplete={!isReadOnly ? handleEditComplete : null}
          removeLabel={`Remove ${media?.name || 'uploaded image'}`}
          editUploadUsage={MediaUploadUsage.ImageStandard.Value}
          editAttachToPhotoManager
          wrapperClassName="w-40 flex-shrink-0"
          frameClassName="aspect-square w-full rounded-lg"
          imageClassName="h-full w-full rounded-lg object-cover"
          triggerProps={mergedLightboxTriggerProps ? undefined : interactionProps}
          lightboxTriggerProps={mergedLightboxTriggerProps}
        />
      </li>
    )
  }

  return typeof media?.id === 'string' || typeof media?.id === 'number' ? (
    <SortableImagePreview id={media.id} disabled={isReadOnly}>
      {renderPreview}
    </SortableImagePreview>
  ) : (
    renderPreview()
  )
}

export default ImagePreview
